import {
  AutoReceiptSetting,
  CardFormFactor,
  CardGroup as RpcCardGroup,
  CardGroupType,
  CardInstance as RpcCardInstance,
  CardReceiptPreferences as RpcCardReceiptPreferences,
  MerchantReceiptOverride as RpcMerchantReceiptOverride,
} from 'rpc/model/squareup/buyerportal/receiptsettings/service';

// eslint-disable-next-line unicorn/prefer-export-from
export { AutoReceiptSetting, CardFormFactor, CardGroupType };

const BRAND_DISPLAY_MAP: Record<string, string> = {
  AMERICAN_EXPRESS: 'American Express',
  CHINA_UNIONPAY: 'China UnionPay',
  DISCOVER: 'Discover',
  INTERAC: 'Interac',
  JCB: 'JCB',
  MASTERCARD: 'MasterCard',
  VISA: 'Visa',
};

function formatBrand(raw: string): string {
  return BRAND_DISPLAY_MAP[raw] ?? raw;
}

export interface DomainCardInstance {
  id: string;
  panSequenceNumber: string;
  formFactor: CardFormFactor;
  cardholderName: string;
  expiration: string;
  isActive: boolean;
}

export interface DomainCardGroup {
  paymentAccountReference: string;
  brand: string;
  lastFour: string;
  type: CardGroupType;
  instances: DomainCardInstance[];
  displayName: string;
}

export interface DomainMerchantOverride {
  merchantId: string;
  merchantDisplayName: string;
  merchantImageUrl: string;
  setting: AutoReceiptSetting;
}

export interface DomainCardReceiptPreferences {
  id: string;
  panSequenceNumber: string;
  autoReceiptSetting: AutoReceiptSetting;
  merchantOverrides: DomainMerchantOverride[];
}

export interface ReceiptSettingsData {
  cardGroups: DomainCardGroup[];
  preferences: DomainCardReceiptPreferences[];
}

export const ReceiptSettingsConverter = {
  fromCardGroup(rpc: RpcCardGroup): DomainCardGroup {
    const brand = formatBrand(rpc.brand);
    return {
      brand,
      displayName: `${brand} ${rpc.lastFour}`,
      instances: (rpc.instances ?? []).map(
        ReceiptSettingsConverter.fromCardInstance
      ),
      lastFour: rpc.lastFour,
      paymentAccountReference: rpc.paymentAccountReference,
      type: rpc.type ?? CardGroupType.CARD_GROUP_TYPE_UNKNOWN,
    };
  },

  fromCardInstance(rpc: RpcCardInstance): DomainCardInstance {
    return {
      cardholderName: rpc.cardholderName ?? '',
      expiration: rpc.expiration ?? '',
      formFactor: rpc.formFactor ?? CardFormFactor.FORM_FACTOR_UNKNOWN,
      id: rpc.id,
      isActive: rpc.isActive ?? true,
      panSequenceNumber: rpc.panSequenceNumber ?? '',
    };
  },

  fromCardReceiptPreferences(
    rpc: RpcCardReceiptPreferences
  ): DomainCardReceiptPreferences {
    return {
      autoReceiptSetting:
        rpc.autoReceiptSetting ?? AutoReceiptSetting.AUTO_RECEIPT_UNKNOWN,
      id: rpc.id,
      merchantOverrides: (rpc.merchantOverrides ?? []).map(
        ReceiptSettingsConverter.fromMerchantOverride
      ),
      panSequenceNumber: rpc.panSequenceNumber ?? '',
    };
  },

  fromMerchantOverride(
    rpc: RpcMerchantReceiptOverride
  ): DomainMerchantOverride {
    return {
      merchantDisplayName: rpc.merchant?.displayName ?? '',
      merchantId: rpc.merchant?.id ?? '',
      merchantImageUrl: rpc.merchant?.imageUrl ?? '',
      setting: rpc.setting ?? AutoReceiptSetting.AUTO_RECEIPT_UNKNOWN,
    };
  },
};

export function getFormFactorLabelKey(
  formFactor: CardFormFactor
): string | null {
  switch (formFactor) {
    case CardFormFactor.DIGITAL_WALLET_APPLE_PAY: {
      return 'profile.receiptSettings.formFactor.applePay';
    }
    case CardFormFactor.DIGITAL_WALLET_GOOGLE_PAY: {
      return 'profile.receiptSettings.formFactor.googlePay';
    }
    case CardFormFactor.DIGITAL_WALLET_OTHER: {
      return 'profile.receiptSettings.formFactor.digitalWallet';
    }
    default: {
      return null;
    }
  }
}

export function getCardGroupLabelKey(type: CardGroupType): string | null {
  switch (type) {
    case CardGroupType.SHARED_FAMILY: {
      return 'profile.receiptSettings.cardGroup.family';
    }
    case CardGroupType.REISSUED: {
      return 'profile.receiptSettings.cardGroup.reissued';
    }
    default: {
      return null;
    }
  }
}

export function isOptedIn(setting: AutoReceiptSetting): boolean {
  return setting === AutoReceiptSetting.AUTO_RECEIPT_OPT_IN;
}

export function findPreferencesForInstance(
  preferences: DomainCardReceiptPreferences[],
  instanceId: string,
  panSequenceNumber?: string
): DomainCardReceiptPreferences | undefined {
  return preferences.find(
    (p) =>
      p.id === instanceId &&
      (panSequenceNumber === undefined ||
        p.panSequenceNumber === panSequenceNumber)
  );
}
